import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Coins, Database, Store, Server, Globe, Lock, Loader2, Rocket, CheckCircle2, TrendingUp } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Vault() {
  const { user } = useApp();
  const [tab, setTab] = useState("data");
  if (!user) return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-32 text-center">
      <Database className="mx-auto text-brand mb-3" />
      <p className="text-sec">Sign in to see your data value, earn from anonymized data, and host your apps.</p>
      <a href="/auth" className="btn-primary h-10 px-6 inline-flex items-center mt-4 text-sm">Sign in</a>
    </div>
  );
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ShieldCheck size={14} /> My Vault</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">You own your data & IP</h1>
        <p className="text-sec mt-3">Why My Kind? Because <b className="text-[var(--rud-text)]">you own your data and you earn from it</b> — unlike big players who take control of your data and give you nothing in return. See what your data is worth, earn credits by sharing anonymized insights with vetted buyers, and host the apps you build — safeguarded with SSL.</p>
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-elevated border border-rud w-fit mt-6">
        <button data-testid="tab-data" onClick={() => setTab("data")} className={`px-5 py-1.5 rounded-full text-sm ${tab === "data" ? "bg-brand text-black" : "text-sec"}`}>Data & IP</button>
        <button data-testid="tab-hosting" onClick={() => setTab("hosting")} className={`px-5 py-1.5 rounded-full text-sm ${tab === "hosting" ? "bg-brand text-black" : "text-sec"}`}>Hosting</button>
      </div>
      {tab === "data" ? <DataTab /> : <HostingTab />}
    </div>
  );
}

function DataTab() {
  const { fmtMoney } = useApp();
  const [val, setVal] = useState(null);
  const [market, setMarket] = useState(null);
  const [price, setPrice] = useState(200);
  const [busy, setBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ org_name: "", industry: "", categories: "", purpose: "", offer_usd: 150 });
  const [posting, setPosting] = useState(false);

  const load = async () => {
    const [v, m, r, mr] = await Promise.all([
      api.get("/data/value"), api.get("/data/marketplace"),
      api.get("/data/requests"), api.get("/data/requests/mine"),
    ]);
    setVal(v.data); setMarket(m.data); setReqs(r.data.requests || []); setMine(mr.data.requests || []);
  };
  useEffect(() => { load(); }, []);

  const listData = async () => { try { await api.post("/data/list", { price_usd: price, categories: [] }); toast.success("Your anonymized data is listed"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const grant = async (id) => { setBusy(true); try { const { data } = await api.post("/data/grant", { offer_id: id }); toast.success(`Earned $${data.earned} (anonymized · demo)`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const grantReq = async (id) => { setBusy(true); try { const { data } = await api.post(`/data/requests/${id}/grant`); toast.success(`Earned $${data.earned} from this org (anonymized · demo)`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const postReq = async () => {
    if (!form.org_name.trim()) { toast.error("Enter your organisation name."); return; }
    setPosting(true);
    try {
      await api.post("/data/requests", {
        org_name: form.org_name, industry: form.industry, purpose: form.purpose,
        offer_usd: Number(form.offer_usd) || 0,
        categories: form.categories.split(",").map((c) => c.trim()).filter(Boolean),
      });
      toast.success("Data request posted — members can now grant access");
      setForm({ org_name: "", industry: "", categories: "", purpose: "", offer_usd: 150 });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setPosting(false); }
  };
  const closeReq = async (id) => { try { await api.post(`/data/requests/${id}/close`); toast.success("Request closed"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div className="mt-6 space-y-4">
      <div className="rud-card rounded-2xl p-5 flex items-start gap-3 border-l-2" style={{ borderLeftColor: "var(--rud-brand)" }}>
        <ShieldCheck className="text-brand shrink-0" />
        <p className="text-sm text-sec">{val?.ownership || "You own 100% of your data and IP."}</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rud-card rounded-2xl p-6"><Coins className="text-brand mb-3" /><div className="text-xs uppercase tracking-widest text-sec">Estimated data value</div><div className="font-serif text-4xl mt-1">${val?.estimated_value_usd ?? "—"}</div></div>
        <div className="rud-card rounded-2xl p-6"><TrendingUp className="text-brand mb-3" /><div className="text-xs uppercase tracking-widest text-sec">Data earnings</div><div className="font-serif text-4xl mt-1">${val?.data_earnings ?? 0}</div></div>
        <div className="rud-card rounded-2xl p-6"><Store className="text-brand mb-3" /><div className="text-xs uppercase tracking-widest text-sec">List your data</div>
          <div className="flex gap-2 mt-2"><input data-testid="data-price" type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="rud-input rounded-lg px-2 py-1.5 text-sm w-24" />
          <button data-testid="data-list" onClick={listData} className="btn-primary text-sm px-3">List</button></div>
          <div className="text-[11px] text-sec mt-1">Anonymized only — no names/identifiers.</div>
        </div>
      </div>
      {val?.breakdown && (
        <div className="rud-card rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-brand mb-3">Value breakdown</div>
          <div className="grid sm:grid-cols-4 gap-2">{val.breakdown.map((b, i) => (
            <div key={i} className="rounded-lg bg-elevated border border-rud p-3"><div className="text-xs text-sec">{b.label}</div><div className="text-sm">{b.count} · {b.points} pts</div></div>
          ))}</div>
        </div>
      )}

      {/* Orgs requesting members' anonymized data */}
      <div data-testid="data-requests-section">
        <h3 className="font-serif text-2xl mb-1">Organisations requesting data</h3>
        <p className="text-sec text-sm mb-3">Grant your anonymized data to an organisation's request and earn the offer instantly.</p>
        {reqs.length === 0 ? (
          <div className="rud-card rounded-2xl p-6 text-sec text-sm">No open requests right now. Check back soon.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="data-requests-list">
            {reqs.map((r) => (
              <div key={r.id} className="rud-card rounded-2xl p-5 flex flex-col" data-testid={`data-request-${r.id}`}>
                <div className="text-xs text-brand uppercase tracking-widest">{r.industry || "Organisation"}</div>
                <div className="font-serif text-2xl mt-1">{r.org_name}</div>
                {r.purpose && <p className="text-sec text-sm mt-1 leading-relaxed">{r.purpose}</p>}
                {r.categories?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{r.categories.map((c, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-rud text-sec">{c}</span>)}</div>}
                <div className="flex items-center justify-between mt-auto pt-4">
                  <span className="text-brand text-lg">{fmtMoney ? fmtMoney(r.offer_usd) : `$${r.offer_usd}`}</span>
                  {r.is_mine ? (
                    <span className="text-[11px] text-sec">Your request · {r.grants_count} granted</span>
                  ) : (
                    <button data-testid={`grant-req-${r.id}`} disabled={r.you_granted || busy} onClick={() => grantReq(r.id)} className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50">
                      {r.you_granted ? <span className="flex items-center gap-1"><CheckCircle2 size={14} /> Granted</span> : "Grant & earn"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Post a request (for orgs / businesses) */}
      <div className="rud-card rounded-2xl p-6" data-testid="data-request-form">
        <div className="flex items-center gap-2 mb-1"><Database className="text-brand" /><h3 className="font-serif text-2xl">Request data for your organisation</h3></div>
        <p className="text-sec text-sm mb-4">Post a request and members earn by granting you their anonymized, aggregated data. No identities — ever.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="req-org-name" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Organisation name" className="rud-input rounded-lg px-3 py-2 text-sm" />
          <input data-testid="req-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Industry (e.g. Retail)" className="rud-input rounded-lg px-3 py-2 text-sm" />
          <input data-testid="req-categories" value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="Data categories, comma-separated" className="rud-input rounded-lg px-3 py-2 text-sm sm:col-span-2" />
          <textarea data-testid="req-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="What will you use this anonymized data for?" rows={2} className="rud-input rounded-lg px-3 py-2 text-sm sm:col-span-2" />
          <div className="flex items-center gap-2">
            <span className="text-sm text-sec">Offer per member ($)</span>
            <input data-testid="req-offer" type="number" value={form.offer_usd} onChange={(e) => setForm({ ...form, offer_usd: e.target.value })} className="rud-input rounded-lg px-2 py-1.5 text-sm w-28" />
          </div>
        </div>
        <button data-testid="req-submit" onClick={postReq} disabled={posting} className="btn-primary h-10 px-6 mt-4 inline-flex items-center gap-2 disabled:opacity-60">
          {posting ? <Loader2 size={15} className="animate-spin" /> : <Store size={15} />} Post request
        </button>
        {mine.length > 0 && (
          <div className="mt-5 border-t border-rud pt-4" data-testid="my-data-requests">
            <div className="text-xs uppercase tracking-widest text-sec mb-2">Your requests</div>
            {mine.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-1.5 text-sm">
                <span>{r.org_name} <span className="text-sec text-xs">· {r.status} · {r.grants_count} granted</span></span>
                {r.status === "open" && <button data-testid={`close-req-${r.id}`} onClick={() => closeReq(r.id)} className="text-xs text-sec hover:text-brand">Close</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-serif text-2xl mb-3">Featured buyers (anonymized data)</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="data-buyers">
          {market?.buyers.map((b) => {
            const granted = market.granted?.includes(b.id);
            return (
              <div key={b.id} className="rud-card rounded-2xl p-5">
                <div className="text-xs text-brand uppercase tracking-widest">{b.industry}</div>
                <div className="font-serif text-2xl mt-1">{b.buyer}</div>
                <p className="text-sec text-sm mt-1">{b.wants}</p>
                <div className="flex items-center justify-between mt-4"><span className="text-brand text-lg">${b.price_usd}</span>
                  <button data-testid={`grant-${b.id}`} disabled={granted || busy} onClick={() => grant(b.id)} className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50">
                    {granted ? <span className="flex items-center gap-1"><CheckCircle2 size={14} /> Granted</span> : "Grant access"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HostingTab() {
  const [q, setQ] = useState("");
  const [domains, setDomains] = useState(null);
  const [plans, setPlans] = useState([]);
  const [sel, setSel] = useState({ app_name: "", domain: "", ssl: true, plan: "Starter" });
  const [busy, setBusy] = useState(false);
  const [deployed, setDeployed] = useState(null);
  const [deployments, setDeployments] = useState([]);

  useEffect(() => { api.get("/hosting/plans").then((r) => setPlans(r.data.plans)); api.get("/hosting/deployments").then((r) => setDeployments(r.data.deployments)); }, []);

  const search = async () => { if (!q.trim()) return; try { const { data } = await api.get(`/hosting/domains?query=${encodeURIComponent(q)}`); setDomains(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const deploy = async () => { if (!sel.app_name.trim()) { toast.error("Name your app."); return; } setBusy(true); try { const { data } = await api.post("/hosting/deploy", sel); setDeployed(data.deployment); toast.success("App deployed (demo)"); const d = await api.get("/hosting/deployments"); setDeployments(d.data.deployments); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-4">
      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><Globe className="text-brand" /><h3 className="font-serif text-2xl">Find a domain</h3>
          <span className="ml-auto text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-yellow-500/15 text-yellow-400">Demo · +36%</span></div>
        <div className="flex gap-2 mt-2">
          <input data-testid="domain-query" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="yourbrand" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="domain-search" onClick={search} className="btn-primary px-5 text-sm">Search</button>
        </div>
        {domains && (
          <div className="mt-3 space-y-2">
            {domains.results.map((d) => (
              <button key={d.domain} data-testid={`domain-${d.tld}`} onClick={() => setSel({ ...sel, domain: d.domain })} className={`w-full flex items-center justify-between rounded-lg p-3 border text-left ${sel.domain === d.domain ? "border-brand bg-white/5" : "border-rud"}`}>
                <span className="text-sm">{d.domain}</span><span className="text-brand text-sm">${d.price_usd}<span className="text-[10px] text-sec">/yr</span></span>
              </button>
            ))}
            <p className="text-[11px] text-sec">Prices include My Kind's 36% service margin.</p>
          </div>
        )}
      </div>

      <div className="rud-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3"><Server className="text-brand" /><h3 className="font-serif text-2xl">Deploy your app</h3></div>
        <input data-testid="app-name" value={sel.app_name} onChange={(e) => setSel({ ...sel, app_name: e.target.value })} placeholder="App name" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
        <div className="mt-2"><Select value={sel.plan} onValueChange={(v) => setSel({ ...sel, plan: v })}>
          <SelectTrigger data-testid="plan-select" className="rud-input rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-rud text-[var(--rud-text)]">{plans.map((p) => <SelectItem key={p.id} value={p.id} className="focus:bg-white/10">{p.name} · ${p.usd_month}/mo · {p.specs}</SelectItem>)}</SelectContent>
        </Select></div>
        <button data-testid="ssl-toggle" onClick={() => setSel({ ...sel, ssl: !sel.ssl })} className={`mt-2 w-full flex items-center gap-3 rounded-lg p-3 border ${sel.ssl ? "border-brand bg-white/5" : "border-rud"}`}>
          <Lock size={16} className="text-brand" /><div className="flex-1 text-left"><div className="text-sm">SSL / HTTPS</div><div className="text-[11px] text-sec">Safeguard your app with encryption</div></div>
          <span className={`w-8 h-4 rounded-full relative ${sel.ssl ? "bg-brand" : "bg-elevated"}`}><span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white ${sel.ssl ? "left-4" : "left-0.5"}`} /></span>
        </button>
        {sel.domain && <p className="text-xs text-sec mt-2">Domain: <span className="text-brand">{sel.domain}</span></p>}
        <button data-testid="deploy-btn" onClick={deploy} disabled={busy} className="btn-primary w-full h-11 mt-3 flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Host on My Kind
        </button>
        {deployed && (
          <div className="mt-3 rounded-lg bg-elevated border border-rud p-3 text-sm" data-testid="deploy-result">
            <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 size={16} /> Live {deployed.ssl && "· SSL"}</div>
            <a href={deployed.url} target="_blank" rel="noreferrer" className="text-brand break-all">{deployed.url}</a>
          </div>
        )}
        {deployments.length > 0 && (
          <div className="mt-4"><div className="text-xs uppercase tracking-widest text-sec mb-2">Your apps</div>
            {deployments.map((d) => <div key={d.id} className="text-xs text-sec flex justify-between py-1"><span>{d.app_name}</span><span className="text-brand">{d.status}</span></div>)}
          </div>
        )}
      </div>
    </div>
  );
}
